import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScholarshipsManagement() {
  const [scholarships, setScholarships] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    percentage: 50,
    max_uses: null,
    valid_from: '',
    valid_until: '',
    campaign_tag: '',
  });
  const { getToken } = useAdminAuth();

  useEffect(() => {
    fetchScholarships();
  }, []);

  const fetchScholarships = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/scholarships`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setScholarships(response.data.scholarships);
    } catch (error) {
      console.error('Error fetching scholarships:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/admin/scholarships`, formData, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setShowCreateModal(false);
      fetchScholarships();
      setFormData({ code: '', percentage: 50, max_uses: null, valid_from: '', valid_until: '', campaign_tag: '' });
      alert('Beca creada correctamente');
    } catch (error) {
      console.error('Error creating scholarship:', error);
      alert(error.response?.data?.detail || 'Error al crear la beca');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Seguro que deseas desactivar esta beca?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/scholarships/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      fetchScholarships();
      alert('Beca desactivada correctamente');
    } catch (error) {
      console.error('Error deleting scholarship:', error);
      alert('Error al desactivar la beca');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Gestión de Becas</h1>
          <p className="text-gray-400 mt-1">Administra los códigos de beca disponibles</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600"
        >
          + Nueva Beca
        </button>
      </div>

      <div className="bg-[#0A0A0A] rounded-lg border border-[#1a4d5c]/30 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-black/50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Código</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Descuento</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Usos</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Vigencia</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Estado</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {scholarships.map((scholarship) => (
                  <tr key={scholarship.id} className="hover:bg-[#1a4d5c]/10">
                    <td className="px-6 py-4">
                      <span className="font-mono text-[#D4AF37] font-bold">{scholarship.code}</span>
                      {scholarship.campaign_tag && (
                        <span className="ml-2 text-xs text-gray-500">({scholarship.campaign_tag})</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white font-semibold">{scholarship.percentage}%</span>
                    </td>
                    <td className="px-6 py-4 text-gray-300">
                      {scholarship.current_uses}
                      {scholarship.max_uses && ` / ${scholarship.max_uses}`}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {scholarship.valid_until ? new Date(scholarship.valid_until).toLocaleDateString('es-ES') : 'Sin límite'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${scholarship.is_active ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                        {scholarship.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {scholarship.is_active && (
                        <button
                          onClick={() => handleDelete(scholarship.id)}
                          className="text-red-500 hover:text-red-400 font-medium"
                        >
                          Desactivar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black bg-opacity-75" onClick={() => setShowCreateModal(false)}></div>
            <div className="relative bg-[#0A0A0A] rounded-lg max-w-lg w-full p-6 border border-[#1a4d5c]/30">
              <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] mb-6">Nueva Beca</h2>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Código</label>
                  <input
                    type="text"
                    required
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="BECA2025"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Porcentaje de Descuento</label>
                  <input
                    type="number"
                    required
                    min="1"
                    max="100"
                    value={formData.percentage}
                    onChange={(e) => setFormData({ ...formData, percentage: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-600 rounded-lg text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Máx. Usos</label>
                    <input
                      type="number"
                      value={formData.max_uses || ''}
                      onChange={(e) => setFormData({ ...formData, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-4 py-2 bg-gray-900/50 border border-gray-600 rounded-lg text-white"
                      placeholder="Ilimitado"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Campaña</label>
                    <input
                      type="text"
                      value={formData.campaign_tag}
                      onChange={(e) => setFormData({ ...formData, campaign_tag: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-900/50 border border-gray-600 rounded-lg text-white"
                      placeholder="Verano2025"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Válido Desde</label>
                    <input
                      type="datetime-local"
                      required
                      value={formData.valid_from}
                      onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-900/50 border border-gray-600 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Válido Hasta</label>
                    <input
                      type="datetime-local"
                      value={formData.valid_until}
                      onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-900/50 border border-gray-600 rounded-lg text-white"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg hover:from-[#F4E4B7] hover:to-[#D4AF37]"
                  >
                    Crear Beca
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
